/**
 * useDraftPersistence — Draft/outline state management and persistence
 *
 * Extracted from MainWorkspace to reduce component size.
 * Manages: save status indicators, outline state with chapter sync,
 * draft text with auto-save to buffers, chapter-switch cleanup,
 * draft creation with version checkpoints, and draft restore from hydration.
 *
 * @module @humanizer/studio/hooks/useDraftPersistence
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { OutlineState } from '../components/workspace/OutlinePanel';
import type { DraftProvenance, DraftMeta } from '../components/workspace/OutlinePanel';
import type { ViewMode } from '../components/workspace/workspace-types';
import { createLogger } from '../utils/debug';

const log = createLogger('Workspace');

export interface UseDraftPersistenceOptions {
  /** Current active chapter ID from BookContext */
  activeChapterId: string | null;
  /** Active chapter object for title */
  activeChapter: { id: string; title: string } | null;
  /** Active project for linking draft buffers */
  activeProject: { id: string; name: string } | null;
  /** All books from BookContext */
  books: Array<{ id: string; title: string; chapters?: unknown[] }>;
  /** Set active book in BookContext */
  setActiveBook: (id: string) => void;
  /** Create a new book */
  createBook: (title: string) => Promise<{ id: string; title: string } | null>;
  /** Create a new chapter in a book */
  createChapter: (title: string, bookId: string) => Promise<{ id: string } | null>;
  /** Save chapter outline data */
  saveChapterOutline: (outline: { sections: Array<{ title: string; cardRefs: number[]; notes: string }>; suggestedArc?: string }) => void;
  /** Save draft buffer ID to chapter */
  saveChapterDraftBuffer: (bufferId: string, chapterId: string) => Promise<void>;
  /** Update chapter metadata (e.g. wordCount, metadata) */
  updateChapter: (chapterId: string, data: { wordCount?: number; metadata?: Record<string, unknown> }) => Promise<void>;
  /** Add buffer to active project */
  addBufferToProject: (bufferId: string) => Promise<void>;
  /** Toast notifications */
  addToast: (message: string, type: 'success' | 'info' | 'warning') => void;
  /** Setter for view mode (ownership remains in MainWorkspace) */
  setViewMode: (mode: ViewMode | ((prev: ViewMode) => ViewMode)) => void;
  /** Restored outline from chapter hydration */
  restoredOutline: unknown;
  /** Consume restored outline */
  consumeRestoredOutline: () => { sections: Array<{ title: string; cardRefs: number[]; notes: string }>; suggestedArc?: string | null } | null;
  /** Restored draft from chapter hydration */
  restoredDraft: unknown;
  /** Consume restored draft */
  consumeRestoredDraft: () => { draftText: string; draftBufferId: string } | null;
}

export interface UseDraftPersistenceReturn {
  // Save status
  saveStatus: 'idle' | 'saving' | 'saved';
  markSaving: () => void;
  markSaved: () => void;
  markSaveFailed: () => void;
  // Outline
  outlineState: OutlineState | null;
  setOutlineState: (state: OutlineState | null) => void;
  // Draft state
  draftText: string;
  setDraftText: (text: string) => void;
  draftSourceCards: Array<{ title: string; excerpt: string }>;
  draftProvenances: DraftProvenance[];
  draftGenerationModel: string | undefined;
  draftHumanizationModel: string | undefined;
  draftHumanized: boolean | undefined;
  draftGenerationTimeMs: number | undefined;
  draftHumanizationTimeMs: number | undefined;
  draftTargetWordCount: number | undefined;
  draftQualityReport: DraftMeta['qualityReport'];
  draftIntentionalDrift: DraftMeta['intentionalDrift'];
  draftBufferId: string | null;
  versionRefreshTrigger: number;
  // Callbacks
  handleDraftFlush: (text: string) => void;
  ensureBookAndChapter: () => Promise<string | null>;
  handleDraftCreatedAndSwitch: (
    text: string,
    sourceCards?: Array<{ title: string; excerpt: string }>,
    provenances?: DraftProvenance[],
    draftMeta?: DraftMeta,
  ) => Promise<void>;
}

export function useDraftPersistence({
  activeChapterId,
  activeChapter,
  activeProject,
  books,
  setActiveBook,
  createBook,
  createChapter,
  saveChapterOutline,
  saveChapterDraftBuffer,
  updateChapter,
  addBufferToProject,
  addToast,
  setViewMode,
  restoredOutline,
  consumeRestoredOutline,
  restoredDraft,
  consumeRestoredDraft,
}: UseDraftPersistenceOptions): UseDraftPersistenceReturn {
  // ─── Save status indicator ─────────────────────────────────────
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const saveStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const markSaving = useCallback(() => {
    setSaveStatus('saving');
    if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
  }, []);

  const markSaved = useCallback(() => {
    setSaveStatus('saved');
    if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
    saveStatusTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
  }, []);

  const markSaveFailed = useCallback(() => {
    setSaveStatus('idle');
    if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
  }, []);

  useEffect(() => {
    return () => {
      if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
    };
  }, []);

  // ─── Outline state ─────────────────────────────────────────────
  const [outlineState, setOutlineState] = useState<OutlineState | null>(null);

  // Restore outline from chapter hydration
  useEffect(() => {
    if (restoredOutline) {
      const outline = consumeRestoredOutline();
      if (outline) {
        setOutlineState({
          sections: outline.sections.map(s => ({
            title: s.title,
            cardRefs: s.cardRefs,
            notes: s.notes,
          })),
          suggestedArc: outline.suggestedArc ?? null,
          arcType: 'auto',
          userOverrodeArc: false,
          stage: outline.sections.length > 0 ? 'editing' : 'initial',
          targetWordCount: 2000,
          verbatimMode: false,
          humanize: true,
          humanizeIntensity: 'moderate',
          humanizeInline: false,
          guidedMode: false,
          checkIntentionalDrift: false,
        } as OutlineState);
      }
    }
  }, [restoredOutline, consumeRestoredOutline]);

  // Auto-save outline to chapter when it changes
  const outlineSaveStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!activeChapterId || !outlineState) return;
    markSaving();
    saveChapterOutline({
      sections: outlineState.sections.map(s => ({
        title: s.title,
        cardRefs: s.cardRefs,
        notes: s.notes,
      })),
      suggestedArc: outlineState.suggestedArc ?? undefined,
    });
    if (outlineSaveStatusTimerRef.current) clearTimeout(outlineSaveStatusTimerRef.current);
    outlineSaveStatusTimerRef.current = setTimeout(() => markSaved(), 1500);
    return () => { if (outlineSaveStatusTimerRef.current) clearTimeout(outlineSaveStatusTimerRef.current); };
  }, [activeChapterId, outlineState, saveChapterOutline, markSaving, markSaved]);

  // ─── Draft state ───────────────────────────────────────────────
  const [draftText, setDraftText] = useState('');
  const [draftSourceCards, setDraftSourceCards] = useState<Array<{ title: string; excerpt: string }>>([]);
  const [draftProvenances, setDraftProvenances] = useState<DraftProvenance[]>([]);
  const [draftGenerationModel, setDraftGenerationModel] = useState<string | undefined>();
  const [draftHumanizationModel, setDraftHumanizationModel] = useState<string | undefined>();
  const [draftHumanized, setDraftHumanized] = useState<boolean | undefined>();
  const [draftGenerationTimeMs, setDraftGenerationTimeMs] = useState<number | undefined>();
  const [draftHumanizationTimeMs, setDraftHumanizationTimeMs] = useState<number | undefined>();
  const [draftTargetWordCount, setDraftTargetWordCount] = useState<number | undefined>();
  const [draftQualityReport, setDraftQualityReport] = useState<DraftMeta['qualityReport']>(undefined);
  const [draftIntentionalDrift, setDraftIntentionalDrift] = useState<DraftMeta['intentionalDrift']>(undefined);
  const draftBufferIdRef = useRef<string | null>(null);
  const [draftBufferId, setDraftBufferId] = useState<string | null>(null);
  const [versionRefreshTrigger, setVersionRefreshTrigger] = useState(0);
  const createdChapterIdRef = useRef<string | null>(null);
  const draftSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftTextRef = useRef(draftText);
  draftTextRef.current = draftText;

  // ─── Chapter-switch cleanup ────────────────────────────────────
  const prevActiveChapterIdRef = useRef(activeChapterId);
  useEffect(() => {
    const prev = prevActiveChapterIdRef.current;
    prevActiveChapterIdRef.current = activeChapterId;

    if (prev === activeChapterId || prev === null) return;

    // Flush outgoing draft buffer before clearing state
    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    const outgoingBufferId = draftBufferIdRef.current;
    const outgoingText = draftTextRef.current;
    if (outgoingBufferId && outgoingText && window.electronBuffer?.load) {
      window.electronBuffer.load(outgoingBufferId).then(buf => {
        if (buf && buf.text !== outgoingText) {
          buf.text = outgoingText;
          buf.wordCount = outgoingText.split(/\s+/).filter(Boolean).length;
          window.electronBuffer!.save(buf);
        }
      }).catch(() => {});
    }

    // Clear all chapter-scoped state
    setDraftText('');
    draftBufferIdRef.current = null;
    setDraftBufferId(null);
    setDraftSourceCards([]);
    setDraftProvenances([]);
    setDraftGenerationModel(undefined);
    setDraftHumanizationModel(undefined);
    setDraftHumanized(undefined);
    setDraftGenerationTimeMs(undefined);
    setDraftHumanizationTimeMs(undefined);
    setDraftTargetWordCount(undefined);
    setDraftQualityReport(undefined);
    setDraftIntentionalDrift(undefined);
    setOutlineState(null);
    createdChapterIdRef.current = null;

    setViewMode(prev => (prev === 'draft' || prev === 'outline') ? 'stack' : prev);
  }, [activeChapterId, setViewMode]);

  // ─── Callbacks ─────────────────────────────────────────────────
  const handleDraftFlush = useCallback((text: string) => {
    draftTextRef.current = text;
  }, []);

  const ensureBookAndChapter = useCallback(async (): Promise<string | null> => {
    const existing = activeChapterId || createdChapterIdRef.current;
    if (existing) {
      log.info('[ensureBookAndChapter] Using existing chapter:', existing);
      return existing;
    }
    if (!activeProject) {
      log.warn('[ensureBookAndChapter] No active project — cannot create chapter');
      return null;
    }
    if (!window.electronBook) {
      log.warn('[ensureBookAndChapter] No electronBook bridge — cannot create chapter');
      return null;
    }

    const bookTitle = activeProject.name || 'Untitled';
    log.info('[ensureBookAndChapter] Looking for book:', bookTitle, 'in', books.length, 'books');
    let book = books.find(b => b.title === bookTitle) ?? null;
    if (book) {
      log.info('[ensureBookAndChapter] Found existing book:', book.id, 'chapters:', book.chapters?.length);
      setActiveBook(book.id);
    } else {
      log.info('[ensureBookAndChapter] Creating new book:', bookTitle);
      book = await createBook(bookTitle);
    }
    if (!book) {
      log.warn('[ensureBookAndChapter] Book creation returned null');
      return null;
    }

    log.info('[ensureBookAndChapter] Creating chapter in book:', book.id);
    const chapter = await createChapter('Chapter 1', book.id);
    if (!chapter) {
      log.warn('[ensureBookAndChapter] Chapter creation returned null');
      return null;
    }
    log.info('[ensureBookAndChapter] Chapter created:', chapter.id);
    createdChapterIdRef.current = chapter.id;
    return chapter.id;
  }, [activeChapterId, activeProject, books, setActiveBook, createBook, createChapter]);

  const handleDraftCreatedAndSwitch = useCallback(async (text: string, sourceCards?: Array<{ title: string; excerpt: string }>, provenances?: DraftProvenance[], draftMeta?: DraftMeta) => {
    setDraftText(text);
    setDraftSourceCards(sourceCards ?? []);
    setDraftProvenances(provenances ?? []);
    setDraftGenerationModel(draftMeta?.generationModel);
    setDraftHumanizationModel(draftMeta?.humanizationModel);
    setDraftHumanized(draftMeta?.humanized);
    setDraftGenerationTimeMs(draftMeta?.generationTimeMs);
    setDraftHumanizationTimeMs(draftMeta?.humanizationTimeMs);
    setDraftTargetWordCount(outlineState?.targetWordCount);
    setDraftQualityReport(draftMeta?.qualityReport);
    setDraftIntentionalDrift(draftMeta?.intentionalDrift);
    setViewMode('draft');

    const wordCount = text.split(/\s+/).filter(Boolean).length;

    log.info('[handleDraftCreatedAndSwitch] Called, electronBuffer:', !!window.electronBuffer?.createFromText);
    if (!window.electronBuffer?.createFromText) return;

    try {
      const targetChapterId = await ensureBookAndChapter();
      log.info('[handleDraftCreatedAndSwitch] ensureBookAndChapter returned:', targetChapterId);

      const existingId = draftBufferIdRef.current;
      let usedExistingBuffer = false;

      // Subsequent draft: checkpoint existing buffer, then overwrite
      if (existingId && window.electronBuffer.load && window.electronBuffer.createVersion) {
        try {
          const existing = await window.electronBuffer.load(existingId);
          if (existing) {
            await window.electronBuffer.createVersion(existingId, 'Previous draft', undefined, 'auto');
            existing.text = text;
            existing.wordCount = wordCount;
            await window.electronBuffer.save(existing);
            usedExistingBuffer = true;
            log.info('[handleDraftCreatedAndSwitch] Checkpointed + overwrote existing buffer:', existingId);
          }
        } catch (err) {
          log.warn('[handleDraftCreatedAndSwitch] Existing buffer load/overwrite failed, falling through to create:', err);
        }
      }

      // First draft (or fallback): create new buffer and link
      if (!usedExistingBuffer) {
        const chapterTitle = activeChapter?.title ?? 'Draft';
        const draftBuffer = await window.electronBuffer.createFromText(text, {
          initialState: 'staged',
          metadata: {
            generatedDraft: true,
            title: `Draft: ${chapterTitle}`,
            draftGenerationMode: draftMeta?.humanized ? 'humanized' : 'normal',
            generationModel: draftMeta?.generationModel,
            passageProvenance: provenances,
            sourceCardBufferIds: sourceCards?.map((_, i) => i),
          },
        });
        draftBufferIdRef.current = draftBuffer.id;
        setDraftBufferId(draftBuffer.id);
        log.info('[handleDraftCreatedAndSwitch] Draft buffer created:', draftBuffer.id);

        if (targetChapterId) {
          await saveChapterDraftBuffer(draftBuffer.id, targetChapterId);
          log.info('[handleDraftCreatedAndSwitch] Draft linked to chapter:', targetChapterId);
        }

        if (activeProject) {
          try { await addBufferToProject(draftBuffer.id); } catch { /* non-fatal */ }
        }
      }

      // Always sync word count + extractedInsights to chapter
      if (targetChapterId) {
        const chapterUpdate: { wordCount: number; metadata?: Record<string, unknown> } = { wordCount };
        if (draftMeta?.extractedInsights) {
          chapterUpdate.metadata = { extractedInsights: draftMeta.extractedInsights };
        }
        await updateChapter(targetChapterId, chapterUpdate);
      }

      setVersionRefreshTrigger(prev => prev + 1);
      addToast('Draft saved to chapter', 'success');
    } catch (err) {
      log.error('[handleDraftCreatedAndSwitch] Auto-save draft to chapter failed:', err);
      addToast('Failed to save draft to chapter', 'warning');
    }
  }, [activeChapter, activeProject, ensureBookAndChapter, saveChapterDraftBuffer, updateChapter, addBufferToProject, addToast, outlineState?.targetWordCount, setViewMode]);

  // ─── Restore draft from chapter hydration ──────────────────────
  useEffect(() => {
    if (restoredDraft) {
      const draft = consumeRestoredDraft();
      if (draft) {
        setDraftText(draft.draftText);
        draftBufferIdRef.current = draft.draftBufferId;
        setDraftBufferId(draft.draftBufferId);
      }
    }
  }, [restoredDraft, consumeRestoredDraft]);

  // ─── Debounced auto-save draft edits ───────────────────────────
  const activeChapterIdRef = useRef(activeChapterId);
  activeChapterIdRef.current = activeChapterId;
  useEffect(() => {
    const bufferId = draftBufferIdRef.current;
    if (!bufferId || !draftText || !window.electronBuffer?.save) return;
    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    markSaving();
    draftSaveTimerRef.current = setTimeout(async () => {
      try {
        const buf = await window.electronBuffer!.load(bufferId);
        if (buf && buf.text !== draftText) {
          buf.text = draftText;
          const wc = draftText.split(/\s+/).filter(Boolean).length;
          buf.wordCount = wc;
          await window.electronBuffer!.save(buf);
          const chId = activeChapterIdRef.current;
          if (chId) {
            updateChapter(chId, { wordCount: wc }).catch(() => {});
          }
        }
        markSaved();
      } catch {
        markSaveFailed();
      }
    }, 2000);
    return () => { if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current); };
  }, [draftText, markSaving, markSaved, markSaveFailed, updateChapter]);

  // ─── Unmount flush ─────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
      const bufferId = draftBufferIdRef.current;
      const text = draftTextRef.current;
      if (!bufferId || !text || !window.electronBuffer?.load) return;
      window.electronBuffer.load(bufferId).then(buf => {
        if (buf && buf.text !== text) {
          buf.text = text;
          buf.wordCount = text.split(/\s+/).filter(Boolean).length;
          window.electronBuffer!.save(buf);
        }
      }).catch(() => {});
    };
  }, []);

  return {
    saveStatus,
    markSaving,
    markSaved,
    markSaveFailed,
    outlineState,
    setOutlineState,
    draftText,
    setDraftText,
    draftSourceCards,
    draftProvenances,
    draftGenerationModel,
    draftHumanizationModel,
    draftHumanized,
    draftGenerationTimeMs,
    draftHumanizationTimeMs,
    draftTargetWordCount,
    draftQualityReport,
    draftIntentionalDrift,
    draftBufferId,
    versionRefreshTrigger,
    handleDraftFlush,
    ensureBookAndChapter,
    handleDraftCreatedAndSwitch,
  };
}
